import React, { useEffect, useState } from "react";

// Chakra imports
import {
  Box,
  Text,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stack,
  Alert,
  AlertIcon,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";

// Custom components
import Card from "components/card/Card.js";
import NFT from "./components/NFT.js";
import imageNotFound from "assets/img/Image_not_available.png";
import { useListContext } from "../../../contexts/listContext";

export default function Products() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const userInfo = JSON.parse(localStorage.getItem("PAuser"));

  const { dateFilter } =
    useListContext();
  
  const [productList, setProductList] = useState([]);
  const [approvedProductList, setApprovedProductList] = useState([]);
  const [deniedProductList, setDeniedProductList] = useState([]);
  const [noActionProductList, setNoActionProductList] = useState([]);

  const [successAlert, setSuccesAlert] = useState("");
  const [failureAlert, setFailureAlert] = useState("");
  const [alertTimeout, setAlertTimeout] = useState(null);

  useEffect(() => {
    return () => {
      if (alertTimeout) {
        clearTimeout(alertTimeout);
      }
    };
  }, [alertTimeout]);

  const handleSuccessAlert = async (alertMessage) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }
    setSuccesAlert(alertMessage);
    const timeout = setTimeout(() => {
      setSuccesAlert("");
    }, 5000);
    setAlertTimeout(timeout);
  };

  const handleFailureAlert = async (alertMessage) => {
    if (alertTimeout) {
      clearTimeout(alertTimeout);
    }
    setFailureAlert(alertMessage);
    const timeout = setTimeout(() => {
      setFailureAlert("");
    }, 5000);
    setAlertTimeout(timeout);
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/lookalike-infringement/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: userInfo?.message?.result.id,
        }),
      });

      const json = await response.json();

      if (response.ok) {
        const list = Array.isArray(json) ? json : [];
        console.log(list);
        setProductList(list);
        setNoActionProductList(list.filter((it) => it.client_status === null || typeof it.client_status === "undefined"));
        setApprovedProductList(list.filter((it) => it.client_status === "1"));
        setDeniedProductList(list.filter((it) => it.client_status === "0"));
      } else if (response.status === 400) {
        handleFailureAlert("An error occurred");
      }
    } catch (e) {
      handleFailureAlert("An error occurred");
    }
  };

  // id: record id, status: 1 approve, 0 deny
  const handleApproval = async (id, status) => {
    try {
      const response = await fetch(`${backendUrl}/api/lookalike-infringement/update-action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id, status }),
      });

      const json = await response.json();

      if (response.ok) {
        // server returns refreshed list; if not, fallback to re-fetch
        const list = Array.isArray(json) ? json : productList;
        setProductList(list);
        setNoActionProductList(list.filter((it) => it.client_status === null || typeof it.client_status === "undefined"));
        setApprovedProductList(list.filter((it) => it.client_status === "1"));
        setDeniedProductList(list.filter((it) => it.client_status === "0"));
        handleSuccessAlert(status === 1 ? "Test Purchase approved" : "Request rejected");
        await fetchList();
      } else {
        handleFailureAlert("An error occurred");
      }
    } catch (e) {
      handleFailureAlert("An error occurred");
    }
  };

  const handleFilter = () => {
    try {
      let allProducts = productList || [];

      let filtered = allProducts;

      // Date filter
      if (dateFilter.startDate && dateFilter.endDate) {
        let startDate = new Date(dateFilter.startDate);
        let endDate = new Date(dateFilter.endDate);
        endDate.setHours(23, 59, 59, 999);

        filtered = filtered.filter((item) => {
          if (!item.detected_date) return false;
          let date = new Date(item.detected_date);
          return date >= startDate && date <= endDate;
        });
      }

      setNoActionProductList(filtered.filter((it) => it.client_status === null || typeof it.client_status === "undefined"));
      setApprovedProductList(filtered.filter((it) => it.client_status === "1"));
      setDeniedProductList(filtered.filter((it) => it.client_status === "0"));
    } catch (err) {
      console.error("Error in filtering products:", err);
      handleFailureAlert("Error in filtering products");
    }
  };

  useEffect(() => {
    handleFilter();
  }, [dateFilter]);

  const renderGrid = (items) => (
    <Box>
      <Card p="0px" w="100%">
        <Text px="20px" py="15px">
          Total Products:{" "}{items.length}
        </Text>
        <SimpleGrid
          columns={{ base: 1, sm: 2, md: 3, xl: 4, "2xl": 5 }}
          spacing={5}
        >
          {items.map((item, index) => (
            <NFT
              key={`${item.id ?? index}`}
              id={item.id}
              name={item.keyword_name}
              infringingName={item.infringing_brand_name}
              image={item.image_url || imageNotFound}
              urls={item.urls}
              documents={item.documents}
              otherDetails={item.other_details}
              clientStatus={item.client_status}
              orderStage={item.order_stage}
              onApprove={(id) => handleApproval(id, 1)}
              onReject={(id) => handleApproval(id, 0)}
            />
          ))}
        </SimpleGrid>
        <br />
      </Card>
    </Box>
  );

  return (
    <Tabs colorScheme="messenger" pt={{ base: "180px", md: "80px", xl: "80px" }}>
      <TabList>
        <Tab border="transparent">Pending Approval</Tab>
        <Tab border="transparent">Approved</Tab>
        <Tab border="transparent">Rejected</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <Box>
            {/* <Card p="0px" w="100%"> */}
            <Flex align={{ sm: "flex-start", lg: "center" }} justify="space-between" w="100%" px="22px" py="18px" />
            {noActionProductList.length===0 ? (<div>No Pending Products for Test Purchase approval</div>) :
            renderGrid(noActionProductList)}
            {/* </Card> */}
            
          </Box>
        </TabPanel>
        <TabPanel>
          <Box>
    
            <Flex align={{ sm: "flex-start", lg: "center" }} justify="space-between" w="100%" px="22px" py="18px" />
            {approvedProductList.length === 0 ? (<div>No Approved Product</div>) : renderGrid(approvedProductList)}
          </Box>
        </TabPanel>
        <TabPanel>
          <Box>
            {/* <Card p="0px" w="100%"> */}
            <Flex align={{ sm: "flex-start", lg: "center" }} justify="space-between" w="100%" px="22px" py="18px" />
            {deniedProductList.length === 0 ? (<div>No Denied Product</div>) :
            renderGrid(deniedProductList)}
            {/* </Card> */}
          </Box>
        </TabPanel>
      </TabPanels>

      <Stack spacing={3} position="fixed" right="10px" bottom="2vh">
        {successAlert !== "" && (
          <Alert status="success" variant="left-accent" w="40vw">
            <AlertIcon />
            {successAlert}
          </Alert>
        )}
        {failureAlert !== "" && (
          <Alert status="error" variant="left-accent" w="40vw">
            <AlertIcon />
            {failureAlert}
          </Alert>
        )}
      </Stack>
    </Tabs>
  );
}
